/**
 * JSON 파일 → 새 프로젝트 계획.
 *
 * 프로젝트 JSON(내보내기)은 프로젝트의 스냅샷이다. 새 프로젝트는 그 **저작 내용**(AUTHORED_PROJECT_KEYS)만 받고,
 * 모델의 파생값(VOLATILE_MODEL_KEYS: id·시각·컴파일 결과)은 새로 찍거나 버린다. 버전 이력·실행 이력은 옛 프로젝트에서
 * 일어난 일이라 가져오지 않는다 — 실행 이력은 옛 프로젝트의 산출물 경로를 가리킨다.
 *
 * 옛 형식(그래프 하나만 담긴 JSON)은 만들지 않는다. 사전 출시라 옛 데이터 호환은 기본값이 아니고, .i 덱은 편집기의
 * Input Deck 열기가 받는다.
 */
import { AUTHORED_PROJECT_KEYS, VOLATILE_MODEL_KEYS } from '@/types/project';
import type { Model, ProjectData, VersionEntry } from '@/types/project';
import { checkVsmrCompatibility, detectProjectFileType } from '@/features/project-home/projectJsonFormat';
import { storedName, validateName } from '@/domain/namePolicy';
import { migrateProjectData } from '@/app/persistence/projectMigration';
import type { VsmrProjectFile } from '@/features/project-home/projectJsonFormat';
import { remapScenarioModelIds } from '@/domain/scenario';

/** 파일을 읽은 결과 — 스냅샷 두 형식과 옛 형식 */
export type ProjectFileRead =
  | { kind: 'vsmr' | 'raw'; projectName: string; data: ProjectData }
  | { kind: 'legacy' };

export interface ProjectModelSummary {
  name: string;
  codes: string;
  nodeCount: number;
  hasText: boolean;
}

export interface ProjectFromFilePlan {
  /** 이름 기본값 — 파일의 프로젝트 이름(raw 는 파일 이름) */
  name: string;
  models: ProjectModelSummary[];
  hasCoSimConfig: boolean;
  /** createProject 에 실을 data */
  data: ProjectData;
}

export interface PlanOptions {
  now: string;
  newId: () => string;
}

const LEGACY_REFUSED =
  '옛 형식(그래프 하나만 담긴 JSON)은 새 프로젝트로 만들지 않습니다 — .i 덱은 편집기의 Input Deck 열기로 가져오세요';

/** 파일을 읽어 계획의 입력으로 — 형식 판별·버전 검사는 projectJsonFormat 한 곳. 옛 형식은 역직렬화하지 않는다(만들지 않으므로) */
export async function readProjectFileForCreate(file: File): Promise<ProjectFileRead | { error: string }> {
  if (!file.name.toLowerCase().endsWith('.json')) return { error: 'JSON 파일만 지원합니다.' };
  let json: Record<string, unknown>;
  try {
    json = JSON.parse(await file.text());
  } catch {
    return { error: '유효하지 않은 JSON 형식입니다.' };
  }
  const type = detectProjectFileType(json);
  if (type === 'vsmr') {
    const vsmr = json as unknown as VsmrProjectFile;
    const compatError = checkVsmrCompatibility(vsmr._vsmr_meta_);
    if (compatError) return { error: compatError };
    // 이름은 창에서 고치는 기본값일 뿐이다 — 래퍼에 이름이 없으면 파일 이름으로(게이트 S3)
    const metaName = typeof vsmr._vsmr_meta_.projectName === 'string' ? vsmr._vsmr_meta_.projectName.trim() : '';
    return { kind: 'vsmr', projectName: metaName || fileStem(file), data: vsmr.data };
  }
  if (type === 'raw-projectdata') {
    return { kind: 'raw', projectName: fileStem(file), data: json as unknown as ProjectData };
  }
  if (type === 'legacy-mars') return { kind: 'legacy' };
  return { error: '지원하지 않는 프로젝트 파일 형식입니다' };
}

const fileStem = (file: File) => file.name.replace(/\.json$/i, '');

function freshModel(source: Model, { now, newId }: PlanOptions): Model {
  const copy = { ...source } as Record<string, unknown>;
  for (const k of VOLATILE_MODEL_KEYS) delete copy[k];
  return {
    ...(copy as unknown as Model),
    id: newId(),
    created_at: now,
    updated_at: now,
    // 모델 이력도 옛 프로젝트에서 일어난 일이다
    updateHistory: [],
    // 저장하는 이름은 다른 표면과 같이(앞뒤 공백 뗌 — namePolicy.storedName)
    name: storedName(source.name ?? ''),
  };
}

export function planProjectFromFile(read: ProjectFileRead, opts: PlanOptions): ProjectFromFilePlan | { error: string } {
  if (read.kind === 'legacy') return { error: LEGACY_REFUSED };
  const source = read.data;
  const models = Array.isArray(source.models) ? source.models : [];
  if (models.length === 0) return { error: '파일에 모델이 없습니다' };
  // 모델 이름 규칙은 새 모델 창·편집 창과 같은 한 곳(namePolicy.validateName: 비어 있지 않고·길이·유일)
  const seen: string[] = [];
  for (const m of models) {
    const nameError = validateName('model', m.name ?? '', seen);
    if (nameError) return { error: `모델 '${m.name ?? ''}': ${nameError}` };
    if (!Array.isArray(m.analysisCodes) || m.analysisCodes.length === 0) {
      return { error: `모델 '${m.name}': 해석 코드가 없습니다` };
    }
    seen.push(m.name);
  }

  // 시나리오 꼴 — 목록이 아니거나, 동작 목록이 없거나, 동작이 아닌 항목(객체가 아니거나 모델 id 가 없는 것)은
  // 모델 경로와 같이 사유로 거절한다(뒤의 재침·프로젝트 열기·시작 창이 던지지 않게)
  if (source.scenarios !== undefined && !Array.isArray(source.scenarios)) return { error: '시나리오 목록(scenarios)의 꼴이 틀립니다' };
  const isAction = (a: unknown): boolean =>
    typeof a === 'object' && a !== null && !Array.isArray(a) && typeof (a as { modelId?: unknown }).modelId === 'string';
  for (const scn of source.scenarios ?? []) {
    if (!Array.isArray(scn?.actions)) return { error: `시나리오 '${scn?.name ?? ''}': 동작 목록(actions)이 없습니다` };
    if (!scn.actions.every(isAction)) return { error: `시나리오 '${scn.name ?? ''}': 동작이 아닌 항목이 있습니다` };
  }

  const created: VersionEntry = {
    version: '1.0',
    timestamp: opts.now,
    author: 'System',
    description: `JSON 파일 «${read.projectName}» 에서 생성`,
  };
  const data: ProjectData = { updateHistory: [created] };
  for (const key of AUTHORED_PROJECT_KEYS) {
    if (key === 'models') continue;
    if (source[key] !== undefined) (data as Record<string, unknown>)[key] = source[key];
  }
  // 시나리오 동작은 모델 id 를 담는다(모델 id 를 내장하는 유일한 저작 키) — 새로 찍은 id 로 되돌려야 가져온 뒤에도 그 모델을 가리킨다
  const idMap = new Map<string, string>();
  data.models = models.map((m) => {
    const fresh = freshModel(m, opts);
    idMap.set(m.id, fresh.id);
    return fresh;
  });
  if (Array.isArray(data.scenarios)) data.scenarios = remapScenarioModelIds(data.scenarios, idMap);

  return {
    // 옛 카탈로그로 내보낸 파일의 scope 는 여기서 공식 id 로 — 서버 읽기 경로(parseProjectData)와 같은 규칙
    name: read.projectName,
    models: models.map((m) => ({
      name: m.name,
      codes: m.analysisCodes.join('/'),
      nodeCount: m.nodes?.length ?? 0,
      hasText: !!m.textContent?.trim(),
    })),
    hasCoSimConfig: source.coSimConfig !== undefined,
    data: migrateProjectData(data),
  };
}
